import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/ui/page-header";
import { Building2, Star, ThumbsUp } from "lucide-react";
import { useUserPermissions } from "@/hooks/use-user-permissions";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CRITERIOS_AVALIACAO_AGENCIA, type CriterioSlug } from "@/types/avaliacao";
import { Skeleton } from "@/components/ui/skeleton";

interface AvaliacaoComPeso {
  user_id: string;
  notas: Record<CriterioSlug, number>;
  evaluation_weight: number;
}

export default function DashboardAgencia() {
  const { agenciaNome, agenciaId, agenciaLogoPath, profileName } = useUserPermissions();

  // Média ponderada dada pela agência aos freelancers
  const { data: avaliacoes = [], isLoading } = useQuery({
    queryKey: ["dashboard-agencia-medias", agenciaId],
    queryFn: async () => {
      if (!agenciaId) return [];

      const { data: usuarios, error: errUsuarios } = await supabase
        .from("profiles")
        .select("id, evaluation_weight")
        .eq("agencia_id", agenciaId);

      if (errUsuarios) throw errUsuarios;
      if (!usuarios || usuarios.length === 0) return [];

      const userIds = usuarios.map((u) => u.id);
      const pesoMap = new Map<string, number>();
      for (const u of usuarios) {
        pesoMap.set(u.id, u.evaluation_weight ?? 5);
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: avaliacoesRaw, error: errAval } = await (supabase as any)
        .from("avaliacoes_usuario_agencia")
        .select("user_id, notas")
        .in("user_id", userIds);

      if (errAval) throw errAval;
      if (!avaliacoesRaw || avaliacoesRaw.length === 0) return [];

      return avaliacoesRaw.map((a: any) => ({
        user_id: a.user_id,
        notas: a.notas as Record<CriterioSlug, number>,
        evaluation_weight: pesoMap.get(a.user_id) ?? 5,
      })) as AvaliacaoComPeso[];
    },
    enabled: !!agenciaId,
    staleTime: 2 * 60 * 1000,
  });

  // Ratings do JotForm (agency → freelancer) via job_evaluations
  const { data: jobRatingsAgency = [], isLoading: isLoadingJobRatings } = useQuery({
    queryKey: ["dashboard-agencia-job-ratings-agency", agenciaId],
    queryFn: async () => {
      if (!agenciaId) return [];

      const { data, error } = await supabase
        .from("job_evaluations")
        .select(`
          rating,
          controle_projetos!inner(agencia_id)
        `)
        .eq("evaluator_type", "agency")
        .eq("origin", "external")
        .eq("controle_projetos.agencia_id", agenciaId)
        .not("rating", "is", null)
        .is("deleted_at", null)
        .is("controle_projetos.deleted_at", null);

      if (error) throw error;
      if (!data || data.length === 0) return [];

      return data.map((avaliacao: any) => avaliacao.rating as number);
    },
    enabled: !!agenciaId,
    staleTime: 2 * 60 * 1000,
  });

  // Média das notas dadas pelos freelancers à agência
  const { data: mediaRecebida, isLoading: isLoadingRecebida } = useQuery({
    queryKey: ["dashboard-agencia-media-recebida", agenciaId],
    queryFn: async () => {
      if (!agenciaId) return null;

      const { data, error } = await supabase
        .from("job_evaluations")
        .select(`
          rating,
          controle_projetos!inner(agencia_id)
        `)
        .eq("evaluator_type", "freelancer")
        .eq("origin", "external")
        .eq("controle_projetos.agencia_id", agenciaId)
        .not("rating", "is", null)
        .is("deleted_at", null)
        .is("controle_projetos.deleted_at", null);

      if (error) throw error;
      if (!data || data.length === 0) return null;

      const ratings = data.map((avaliacao: any) => avaliacao.rating as number);
      const soma = ratings.reduce((acc: number, nota: number) => acc + nota, 0);
      return { media: soma / ratings.length, total: ratings.length };
    },
    enabled: !!agenciaId,
    staleTime: 2 * 60 * 1000,
  });

  // Média geral ponderada: avaliações dos membros + ratings JotForm (agency)
  const mediaGeral = useMemo(() => {
    if (avaliacoes.length === 0 && jobRatingsAgency.length === 0) return null;

    let somaNotasPonderadas = 0;
    let somaPesos = 0;
    const slugs = CRITERIOS_AVALIACAO_AGENCIA.map((c) => c.slug);

    for (const av of avaliacoes) {
      const peso = av.evaluation_weight;
      const mediaAvaliador =
        slugs.reduce((sum, slug) => sum + (av.notas[slug] ?? 5), 0) / slugs.length;

      somaNotasPonderadas += mediaAvaliador * peso;
      somaPesos += peso;
    }

    // Job evaluations (agency → freelancer) com peso 5
    for (const rating of jobRatingsAgency) {
      somaNotasPonderadas += rating * 5;
      somaPesos += 5;
    }

    return somaPesos > 0 ? somaNotasPonderadas / somaPesos : null;
  }, [avaliacoes, jobRatingsAgency]);

  return (
    <div className="space-y-6 fade-in">
      <PageHeader title="Dashboard" subtitle="Portal da agência" />

      {/* Boas-vindas (banner leve) */}
      <div className="flex flex-col gap-3 rounded-lg border bg-muted/30 p-4 sm:flex-row sm:items-center sm:gap-4">
        {agenciaLogoPath ? (
          <img
            src={agenciaLogoPath}
            alt={agenciaNome || "Agência"}
            className="h-10 max-w-[120px] object-contain"
          />
        ) : (
          <Building2 className="h-10 w-10 shrink-0 text-primary" />
        )}
        <div className="min-w-0">
          <h2 className="text-xl font-bold tracking-tight sm:text-2xl">
            {agenciaNome || "Agência"}
          </h2>
          <p className="text-sm text-muted-foreground">{profileName}</p>
        </div>
      </div>

      {/* Médias lado a lado */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Nota dada aos freelancers */}
        <Card className="border-l-4 border-l-yellow-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Nota dada aos freelancers</CardTitle>
            <Star className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            {isLoading || isLoadingJobRatings ? (
              <Skeleton className="h-12 w-24" />
            ) : mediaGeral !== null ? (
              <>
                <div className="text-4xl font-bold">{mediaGeral.toFixed(1)}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Baseada em {avaliacoes.length + jobRatingsAgency.length} avaliação{avaliacoes.length + jobRatingsAgency.length !== 1 ? "ões" : ""} dos membros da agência
                </p>
              </>
            ) : (
              <>
                <div className="text-4xl font-bold text-muted-foreground">-</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Nenhuma avaliação de freelancer registrada ainda
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Nota recebida dos freelancers */}
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Nota recebida dos freelancers</CardTitle>
            <ThumbsUp className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            {isLoadingRecebida ? (
              <Skeleton className="h-12 w-24" />
            ) : mediaRecebida ? (
              <>
                <div className="text-4xl font-bold">{mediaRecebida.media.toFixed(1)}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Baseada em {mediaRecebida.total} avaliação{mediaRecebida.total !== 1 ? "ões" : ""} de freelancers
                </p>
              </>
            ) : (
              <>
                <div className="text-4xl font-bold text-muted-foreground">-</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Nenhuma avaliação recebida de freelancers ainda
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
